export const MODERN_ART_ARTISTS = [
  { id: "nova", name: "Nova Vale", color: "#ff5a5f", count: 12 },
  { id: "mori", name: "Mori Kade", color: "#f6bd2a", count: 13 },
  { id: "lumen", name: "Lumen Grey", color: "#54c6eb", count: 14 },
  { id: "sable", name: "Sable Hart", color: "#8d6cdf", count: 15 },
  { id: "orio", name: "Orio Moss", color: "#5cc98a", count: 16 },
] as const;

export type ModernArtArtistId = typeof MODERN_ART_ARTISTS[number]["id"];
export type ModernArtAuctionType = "open" | "once" | "hidden" | "fixed" | "double";
export type ModernArtStatus = "setup" | "playing" | "finished";

export type ModernArtCard = {
  id: string;
  artistId: ModernArtArtistId;
  auctionType: ModernArtAuctionType;
  number: number;
};

export type ModernArtAuction = {
  originalAuctioneerId: string;
  auctioneerId: string;
  cards: ModernArtCard[];
  type: ModernArtAuctionType | "double_offer";
  highBid: number;
  highBidderId: string | null;
  bids: Record<string, number>;
  passedIds: string[];
  currentActorId: string | null;
  fixedPrice: number | null;
  doubleCandidateIds: string[];
  doubleCandidateIndex: number;
};

export type ModernArtRoundResult = {
  round: number;
  counts: Record<ModernArtArtistId, number>;
  rankings: Array<{ artistId: ModernArtArtistId; rank: 1 | 2 | 3; tile: 30 | 20 | 10; value: number }>;
  payouts: Record<string, number>;
};

export type ModernArtState = {
  status: ModernArtStatus;
  round: number;
  playerOrder: string[];
  auctioneerIndex: number;
  deck: ModernArtCard[];
  hands: Record<string, ModernArtCard[]>;
  money: Record<string, number>;
  purchased: Record<string, ModernArtCard[]>;
  offeredCounts: Record<ModernArtArtistId, number>;
  valueTiles: Record<ModernArtArtistId, number[]>;
  auction: ModernArtAuction | null;
  lastRound: ModernArtRoundResult | null;
  winnerIds: string[];
  turnNumber: number;
  log: string[];
};

export type ModernArtAction =
  | { type: "start" }
  | { type: "offer"; cardId: string }
  | { type: "double_second"; cardId: string }
  | { type: "double_pass" }
  | { type: "bid"; amount: number }
  | { type: "pass" }
  | { type: "close" }
  | { type: "set_price"; amount: number }
  | { type: "buy" };

const AUCTION_TYPE_COUNTS: Record<number, Record<ModernArtAuctionType, number>> = {
  12: { open: 2, once: 3, hidden: 2, fixed: 3, double: 2 },
  13: { open: 3, once: 2, hidden: 3, fixed: 3, double: 2 },
  14: { open: 3, once: 3, hidden: 3, fixed: 3, double: 2 },
  15: { open: 3, once: 3, hidden: 3, fixed: 3, double: 3 },
  16: { open: 4, once: 3, hidden: 3, fixed: 3, double: 3 },
};
const DEALS: Record<number, [number, number, number, number]> = { 3: [10, 6, 6, 0], 4: [9, 4, 4, 0], 5: [8, 3, 3, 0] };

const emptyCounts = (): Record<ModernArtArtistId, number> => ({ nova: 0, mori: 0, lumen: 0, sable: 0, orio: 0 });

export function createModernArtDeck(): ModernArtCard[] {
  return MODERN_ART_ARTISTS.flatMap((artist) => {
    const types = (Object.entries(AUCTION_TYPE_COUNTS[artist.count]) as Array<[ModernArtAuctionType, number]>)
      .flatMap(([type, count]) => Array<ModernArtAuctionType>(count).fill(type));
    return types.map((auctionType, index) => ({ id: `${artist.id}-${index + 1}`, artistId: artist.id, auctionType, number: index + 1 }));
  });
}

export function shuffleModernArtDeck(deck: ModernArtCard[], random = Math.random) {
  for (let index = deck.length - 1; index > 0; index -= 1) {
    const swap = Math.floor(random() * (index + 1));
    [deck[index], deck[swap]] = [deck[swap], deck[index]];
  }
  return deck;
}

export function createInitialModernArtState(): ModernArtState {
  return {
    status: "setup", round: 0, playerOrder: [], auctioneerIndex: 0, deck: [], hands: {}, money: {}, purchased: {},
    offeredCounts: emptyCounts(), valueTiles: { nova: [], mori: [], lumen: [], sable: [], orio: [] },
    auction: null, lastRound: null, winnerIds: [], turnNumber: 0, log: [],
  };
}

export function hydrateModernArtState(value: Partial<ModernArtState> | null | undefined): ModernArtState {
  return { ...createInitialModernArtState(), ...(value || {}), offeredCounts: { ...emptyCounts(), ...(value?.offeredCounts || {}) },
    valueTiles: { nova: [], mori: [], lumen: [], sable: [], orio: [], ...(value?.valueTiles || {}) } };
}

const clockwise = (state: ModernArtState, startId: string, includeStart = false) => {
  const start = state.playerOrder.indexOf(startId);
  if (start < 0) return [];
  return Array.from({ length: state.playerOrder.length }, (_, offset) => state.playerOrder[(start + offset + (includeStart ? 0 : 1)) % state.playerOrder.length]);
};

const currentAuctioneerId = (state: ModernArtState) => state.playerOrder[state.auctioneerIndex] || null;
const validAmount = (value: unknown) => Number.isInteger(value) && Number(value) >= 0;
const allHandsEmpty = (state: ModernArtState) => state.playerOrder.every((id) => !state.hands[id]?.length);
const skipEmptyAuctioneers = (state: ModernArtState) => {
  if (allHandsEmpty(state)) return;
  let guard = state.playerOrder.length;
  while (guard-- > 0 && !(state.hands[currentAuctioneerId(state) || ""] || []).length) state.auctioneerIndex = (state.auctioneerIndex + 1) % state.playerOrder.length;
};
const removeCard = (state: ModernArtState, playerId: string, cardId: string) => {
  const hand = state.hands[playerId] || [];
  const index = hand.findIndex((card) => card.id === cardId);
  if (index < 0) return null;
  return hand.splice(index, 1)[0];
};
const addLog = (state: ModernArtState, message: string) => { state.log.push(message); state.log = state.log.slice(-24); };

function deal(state: ModernArtState, amount: number) {
  for (let count = 0; count < amount; count += 1) {
    for (const playerId of state.playerOrder) {
      const card = state.deck.pop();
      if (card) state.hands[playerId].push(card);
    }
  }
}

export function startModernArt(state: ModernArtState, playerIds: string[], random = Math.random) {
  if (state.status !== "setup" || playerIds.length < 3 || playerIds.length > 5 || new Set(playerIds).size !== playerIds.length) return false;
  Object.assign(state, createInitialModernArtState());
  state.status = "playing"; state.round = 1; state.playerOrder = [...playerIds]; state.deck = shuffleModernArtDeck(createModernArtDeck(), random);
  for (const id of playerIds) { state.hands[id] = []; state.money[id] = 100; state.purchased[id] = []; }
  deal(state, DEALS[playerIds.length][0]);
  addLog(state, "Season 1 opens. Every gallery begins with $100k.");
  return true;
}

function startAuction(state: ModernArtState, originalAuctioneerId: string, auctioneerId: string, cards: ModernArtCard[], type: ModernArtAuctionType) {
  const order = clockwise(state, auctioneerId);
  state.auction = {
    originalAuctioneerId, auctioneerId, cards, type, highBid: 0, highBidderId: null, bids: {}, passedIds: [],
    currentActorId: type === "once" || type === "fixed" ? order[0] : null, fixedPrice: null,
    doubleCandidateIds: [], doubleCandidateIndex: 0,
  };
}

function fifthEndsRound(state: ModernArtState, card: ModernArtCard) {
  state.offeredCounts[card.artistId] += 1;
  return state.offeredCounts[card.artistId] >= 5;
}

function advanceAuctioneer(state: ModernArtState, auctioneerId: string) {
  const index = state.playerOrder.indexOf(auctioneerId);
  state.auctioneerIndex = index < 0 ? 0 : (index + 1) % state.playerOrder.length;
  state.turnNumber += 1;
  state.auction = null;
  if (allHandsEmpty(state)) finishRound(state);
  else skipEmptyAuctioneers(state);
}

function rankArtists(state: ModernArtState) {
  return MODERN_ART_ARTISTS.filter((artist) => state.offeredCounts[artist.id] > 0)
    .sort((a, b) => state.offeredCounts[b.id] - state.offeredCounts[a.id] || MODERN_ART_ARTISTS.indexOf(a) - MODERN_ART_ARTISTS.indexOf(b))
    .slice(0, 3);
}

function finishRound(state: ModernArtState) {
  const ranked = rankArtists(state);
  const tiles = [30, 20, 10] as const;
  const payouts: Record<string, number> = {};
  const rankings: ModernArtRoundResult["rankings"] = [];
  ranked.forEach((artist, index) => {
    const tile = tiles[index]; state.valueTiles[artist.id].push(tile);
    rankings.push({ artistId: artist.id, rank: (index + 1) as 1 | 2 | 3, tile, value: state.valueTiles[artist.id].reduce((sum, value) => sum + value, 0) });
  });
  for (const playerId of state.playerOrder) {
    payouts[playerId] = state.purchased[playerId].reduce((total, card) => {
      const current = rankings.find((rank) => rank.artistId === card.artistId);
      return total + (current?.value || 0);
    }, 0);
    state.money[playerId] += payouts[playerId];
    state.purchased[playerId] = [];
  }
  state.lastRound = { round: state.round, counts: { ...state.offeredCounts }, rankings, payouts };
  addLog(state, `Season ${state.round} closes. ${rankings.map((rank) => `${rank.artistId} $${rank.value}k`).join(", ")}.`);
  state.auction = null;
  if (state.round >= 4) {
    state.status = "finished";
    const best = Math.max(...state.playerOrder.map((id) => state.money[id]));
    state.winnerIds = state.playerOrder.filter((id) => state.money[id] === best);
    return;
  }
  state.round += 1; state.offeredCounts = emptyCounts();
  deal(state, DEALS[state.playerOrder.length][state.round - 1]);
  skipEmptyAuctioneers(state);
}

function settleAuction(state: ModernArtState, winnerId: string | null, amount: number) {
  const auction = state.auction;
  if (!auction) return;
  const winner = winnerId || auction.auctioneerId;
  const paid = winnerId ? amount : 0;
  if (paid > state.money[winner]) return;
  state.money[winner] -= paid;
  if (paid > 0 && winner !== auction.auctioneerId) state.money[auction.auctioneerId] += paid;
  state.purchased[winner].push(...auction.cards);
  addLog(state, `${winner} acquired ${auction.cards.length} painting${auction.cards.length === 1 ? "" : "s"} for $${paid}k.`);
  advanceAuctioneer(state, auction.auctioneerId);
}

function nextSequentialActor(state: ModernArtState, actorId: string) {
  const auction = state.auction!;
  const order = clockwise(state, auction.auctioneerId);
  const index = order.indexOf(actorId);
  return index >= 0 ? order[index + 1] || null : null;
}

export function applyModernArtAction(state: ModernArtState, playerId: string, action: ModernArtAction): boolean {
  if (action.type === "start") return false;
  if (state.status !== "playing" || !state.playerOrder.includes(playerId)) return false;
  if (!state.auction) {
    if (action.type !== "offer" || currentAuctioneerId(state) !== playerId) return false;
    const card = removeCard(state, playerId, action.cardId); if (!card) return false;
    if (fifthEndsRound(state, card)) { state.auctioneerIndex = (state.playerOrder.indexOf(playerId) + 1) % state.playerOrder.length; addLog(state, `${card.artistId}'s fifth painting closes the season unsold.`); finishRound(state); return true; }
    if (allHandsEmpty(state)) { state.auctioneerIndex = (state.playerOrder.indexOf(playerId) + 1) % state.playerOrder.length; addLog(state, "The final painting closes the season unsold."); finishRound(state); return true; }
    if (card.auctionType === "double") {
      const candidates = [playerId, ...clockwise(state, playerId)];
      state.auction = { originalAuctioneerId: playerId, auctioneerId: playerId, cards: [card], type: "double_offer", highBid: 0, highBidderId: null,
        bids: {}, passedIds: [], currentActorId: playerId, fixedPrice: null, doubleCandidateIds: candidates, doubleCandidateIndex: 0 };
    } else startAuction(state, playerId, playerId, [card], card.auctionType);
    addLog(state, `${playerId} offered a ${card.auctionType} painting by ${card.artistId}.`);
    return true;
  }
  const auction = state.auction;
  if (auction.type === "double_offer") {
    if (auction.currentActorId !== playerId) return false;
    if (action.type === "double_second") {
      const card = (state.hands[playerId] || []).find((item) => item.id === action.cardId);
      if (!card || card.artistId !== auction.cards[0].artistId || card.auctionType === "double") return false;
      removeCard(state, playerId, card.id);
      if (fifthEndsRound(state, card)) { state.auctioneerIndex = (state.playerOrder.indexOf(playerId) + 1) % state.playerOrder.length; addLog(state, "A double auction triggered the fifth painting; both paintings remain unsold."); finishRound(state); return true; }
      if (allHandsEmpty(state)) { state.auctioneerIndex = (state.playerOrder.indexOf(playerId) + 1) % state.playerOrder.length; addLog(state, "The final double closes the season with both paintings unsold."); finishRound(state); return true; }
      startAuction(state, auction.originalAuctioneerId, playerId, [auction.cards[0], card], card.auctionType);
      return true;
    }
    if (action.type !== "double_pass") return false;
    auction.doubleCandidateIndex += 1;
    if (auction.doubleCandidateIndex >= auction.doubleCandidateIds.length) { settleAuction(state, null, 0); return true; }
    auction.currentActorId = auction.doubleCandidateIds[auction.doubleCandidateIndex];
    return true;
  }
  if (auction.type === "open") {
    if (action.type === "bid") {
      if (!validAmount(action.amount) || action.amount <= auction.highBid || action.amount > state.money[playerId]) return false;
      auction.highBid = action.amount; auction.highBidderId = playerId; auction.bids[playerId] = action.amount; return true;
    }
    if (action.type === "pass" && auction.bids[playerId] === undefined) { auction.bids[playerId] = 0; auction.passedIds.push(playerId); return true; }
    if (action.type === "close" && playerId === auction.auctioneerId) { settleAuction(state, auction.highBidderId, auction.highBid); return true; }
    return false;
  }
  if (auction.type === "hidden") {
    if (action.type !== "bid" || auction.bids[playerId] !== undefined || !validAmount(action.amount) || action.amount > state.money[playerId]) return false;
    auction.bids[playerId] = action.amount;
    if (Object.keys(auction.bids).length === state.playerOrder.length) {
      const tieOrder = [auction.auctioneerId, ...clockwise(state, auction.auctioneerId)];
      const winner = [...state.playerOrder].sort((a, b) => auction.bids[b] - auction.bids[a] || tieOrder.indexOf(a) - tieOrder.indexOf(b))[0];
      const amount = auction.bids[winner]; settleAuction(state, amount > 0 ? winner : null, amount);
    }
    return true;
  }
  if (auction.type === "fixed") {
    if (auction.fixedPrice === null) {
      if (action.type !== "set_price" || playerId !== auction.auctioneerId || !validAmount(action.amount) || action.amount > state.money[playerId]) return false;
      auction.fixedPrice = action.amount; return true;
    }
    if (auction.currentActorId !== playerId) return false;
    if (action.type === "buy") { if (auction.fixedPrice > state.money[playerId]) return false; settleAuction(state, playerId, auction.fixedPrice); return true; }
    if (action.type !== "pass") return false;
    const next = nextSequentialActor(state, playerId);
    if (!next) settleAuction(state, auction.auctioneerId, auction.fixedPrice);
    else auction.currentActorId = next;
    return true;
  }
  if (auction.type === "once") {
    if (auction.currentActorId !== playerId || (action.type !== "pass" && action.type !== "bid")) return false;
    if (action.type === "bid") {
      if (!validAmount(action.amount) || action.amount <= auction.highBid || action.amount > state.money[playerId]) return false;
      auction.highBid = action.amount; auction.highBidderId = playerId; auction.bids[playerId] = action.amount;
    } else auction.passedIds.push(playerId);
    const next = nextSequentialActor(state, playerId);
    if (!next) settleAuction(state, auction.highBidderId, auction.highBid);
    else auction.currentActorId = next;
    return true;
  }
  return false;
}

export function modernArtPublicView(state: ModernArtState, viewerId?: string) {
  const isPlayer = Boolean(viewerId && state.playerOrder.includes(viewerId));
  const myHand = isPlayer ? state.hands[viewerId!] || [] : [];
  const auction = state.auction ? {
    ...state.auction,
    bids: state.auction.type === "hidden" ? (state.status === "finished" ? state.auction.bids : viewerId && state.auction.bids[viewerId] !== undefined ? { [viewerId]: state.auction.bids[viewerId] } : {}) : state.auction.bids,
  } : null;
  const activeId = currentAuctioneerId(state);
  const canAct = Boolean(isPlayer && state.auction && (
    state.auction.type === "open"
    || state.auction.type === "hidden" && state.auction.bids[viewerId!] === undefined
    || state.auction.type === "fixed" && state.auction.fixedPrice === null && state.auction.auctioneerId === viewerId
    || state.auction.currentActorId === viewerId
  ));
  return {
    status: state.status, round: state.round, playerOrder: state.playerOrder, auctioneerId: activeId, hand: myHand,
    handCounts: Object.fromEntries(state.playerOrder.map((id) => [id, state.hands[id]?.length || 0])),
    money: state.status === "finished" ? state.money : isPlayer ? { [viewerId!]: state.money[viewerId!] } : {},
    purchased: Object.fromEntries(state.playerOrder.map((id) => [id, state.purchased[id] || []])),
    offeredCounts: state.offeredCounts, valueTiles: state.valueTiles, auction, lastRound: state.lastRound,
    winnerIds: state.winnerIds, turnNumber: state.turnNumber, log: state.log,
    canOffer: Boolean(isPlayer && !state.auction && activeId === viewerId && state.status === "playing"),
    canAct,
  };
}

export function modernArtScore(state: ModernArtState, playerId: string) { return state.money[playerId] || 0; }
